import { Player } from '../types'
import { Award, GitCompareArrows } from 'lucide-react'
import { calculateAge } from '../utils/age'

interface PlayerCardProps {
  player: Player
  onViewProfile: (id: string) => void
  onCompare?: (id: string) => void
  isCompared?: boolean
  adminActions?: React.ReactNode
  rating?: number
}

// OVR calculation helper based on stats
const calculateOVR = (p: Player) => {
  const potentialBase = (p.potential || 3) * 15 + 15 // 3 -> 60, 4 -> 75, 5 -> 90
  const statsAvg = ((p.technical || 5) + (p.physical || 5) + (p.mental || 5)) / 3 // 1-10 scale
  const ovr = Math.round(potentialBase + (statsAvg - 5) * 2.5)
  return Math.min(99, Math.max(50, ovr))
}

const getShortPos = (pos?: string) => {
  if (!pos) return '??'
  if (pos.length <= 3) return pos.toUpperCase()
  return pos
    .split(' ')
    .map(word => (word ? word[0] : ''))
    .join('')
    .toUpperCase()
}

const getAttributeStats = (p: Player, pos: string) => {
  const normPos = pos.toUpperCase()
  const tech = p.technical || 5
  const phys = p.physical || 5
  const ment = p.mental || 5

  if (normPos.includes('GK')) {
    return [
      { label: 'REF', val: Math.round(phys * 8 + 40) },
      { label: 'DIV', val: Math.round(tech * 7 + 45) },
      { label: 'HAN', val: Math.round(tech * 6 + 50) },
      { label: 'POS', val: Math.round(ment * 8 + 40) }
    ]
  }

  if (['CB', 'RB', 'LB', 'RWB', 'LWB', 'DF'].some(x => normPos.includes(x))) {
    return [
      { label: 'DEF', val: Math.round(tech * 8 + 40) },
      { label: 'PHY', val: Math.round(phys * 8 + 40) },
      { label: 'PAC', val: Math.round(phys * 7 + 45) },
      { label: 'ACC', val: Math.round(phys * 6 + 50) }
    ]
  }

  if (['CDM', 'CM', 'CAM', 'MF'].some(x => normPos.includes(x))) {
    return [
      { label: 'PAS', val: Math.round(tech * 8 + 40) },
      { label: 'VIS', val: Math.round(ment * 8 + 40) },
      { label: 'DEF', val: Math.round(tech * 6 + 50) },
      { label: 'STA', val: Math.round(phys * 8 + 40) }
    ]
  }

  return [
    { label: 'PAC', val: Math.round(phys * 8 + 40) },
    { label: 'DRI', val: Math.round(tech * 8 + 40) },
    { label: 'SHO', val: Math.round(tech * 7 + 45) },
    { label: 'PHY', val: Math.round(phys * 7 + 45) }
  ]
}

const getBottomTag = (p: Player, pos: string) => {
  const normPos = pos.toUpperCase()
  if (p.is_top_talent) return '⭐ Wonderkid Status'
  if (p.potential >= 5) return '🔥 Elite Prospect'
  if (normPos.includes('GK')) {
    return `${p.clean_sheets || 9} Clean Sheets`
  }
  if (['CB', 'RB', 'LB', 'RWB', 'LWB', 'DF'].some(x => normPos.includes(x))) {
    return `87% Duel Win`
  }
  if (['CDM', 'CM', 'CAM', 'MF'].some(x => normPos.includes(x))) {
    return 'Key Playmaker'
  }
  if (p.goals > 0) {
    return `${p.goals} Goals in ${p.apps || p.goals + 2} starts`
  }
  return 'High Work Rate'
}

const getMobileHighlightedStat = (p: Player, pos: string) => {
  const normPos = pos.toUpperCase()
  if (p.is_top_talent) return 'Top SE'
  if (normPos.includes('GK')) {
    return '79% save'
  }
  if (['CB', 'RB', 'LB', 'RWB', 'LWB', 'DF'].some(x => normPos.includes(x))) {
    return '8.2 duels'
  }
  if (['CDM', 'CM', 'CAM', 'MF'].some(x => normPos.includes(x))) {
    return 'Top SE'
  }
  return '+14% form'
}

export default function PlayerCard({ player, onViewProfile, onCompare, isCompared, adminActions, rating }: PlayerCardProps) {
  const resolvedPosition = player.main_position || player.positions?.[0] || 'N/A'
  const displayAge = player.date_of_birth ? calculateAge(player.date_of_birth) : player.age

  // OVR 100-scale and 10-scale calculations
  const ovr100 = rating && rating > 10 ? Math.round(rating) : (rating ? Math.round(rating * 10) : calculateOVR(player))
  const rating10 = rating && rating <= 10 ? rating.toFixed(1) : (ovr100 / 10).toFixed(1)

  const stats = getAttributeStats(player, resolvedPosition)
  const bottomTag = getBottomTag(player, resolvedPosition)
  const mobileHighlightedStat = getMobileHighlightedStat(player, resolvedPosition)

  return (
    <div
      onClick={() => onViewProfile(player.id)}
      className="bg-[#0b141c]/90 rounded-[20px] border border-white/5 relative group transition-all duration-300 hover:border-white/10 hover:shadow-2xl hover:shadow-[#7cff01]/5 cursor-pointer select-none overflow-hidden"
    >
      {onCompare && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onCompare(player.id)
          }}
          className={`absolute right-2 top-2 z-20 inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[9px] font-black uppercase tracking-[0.18em] transition ${
            isCompared
              ? 'border-[#7cff01]/60 bg-[#7cff01]/15 text-[#7cff01]'
              : 'border-white/10 bg-slate-950/80 text-slate-200 hover:border-[#7cff01]/40 hover:text-[#7cff01]'
          }`}
          aria-label={`Compare ${player.name}`}
        >
          <GitCompareArrows size={10} />
          {isCompared ? 'On' : 'Add'}
        </button>
      )}

      {adminActions && (
        <div className="absolute top-2 left-2 z-20 flex gap-1.5" onClick={(e) => e.stopPropagation()}>
          {adminActions}
        </div>
      )}

      <div className="block md:hidden">
        <div className="flex flex-col p-3 justify-between h-[155px]">
          <div className="flex items-start gap-2.5 min-w-0">
            <div className="w-14 h-14 rounded-[12px] overflow-hidden bg-gray-950 border border-white/10 flex-shrink-0">
              <img
                src={player.photo_url || '/placeholder-user.png'}
                alt={player.name}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between w-full gap-1">
                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                  <span className="bg-[#7cff01]/10 text-[#7cff01] text-[9px] font-black uppercase px-1.5 py-0.5 rounded-[4px] flex-shrink-0">
                    {getShortPos(resolvedPosition)}
                  </span>
                  <h4 className="text-sm font-black text-white truncate min-w-0 flex-1 pr-1">
                    {player.name}
                  </h4>
                </div>

                <div className="bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[10px] font-black px-1.5 py-0.5 rounded-[6px] flex items-center gap-0.5 flex-shrink-0">
                  <span className="text-[9px]">⭐</span>
                  {rating10}
                </div>
              </div>

              <p className="text-[10px] text-gray-500 truncate mt-1">
                {player.club || 'Independent'}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-white/5 pt-2.5 mt-2">
            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
              {displayAge}y • {player.height ? `${player.height}m` : '1.85m'}
            </span>

            <span className="text-[#7cff01] text-[10px] font-black uppercase tracking-wider">
              {mobileHighlightedStat}
            </span>
          </div>
        </div>
      </div>

      <div className="hidden md:block">
        <div className="flex flex-col p-5 justify-between h-[230px]">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-2xl overflow-hidden bg-gray-950 border border-white/10 flex-shrink-0">
                <img
                  src={player.photo_url || '/placeholder-user.png'}
                  alt={player.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-white text-base font-black truncate max-w-[130px]">
                    {player.name}
                  </span>
                  <span className="text-[#7cff01] text-xs font-black uppercase">
                    {getShortPos(resolvedPosition)}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {displayAge} yrs • {player.club || 'Independent'}
                </p>
              </div>
            </div>

            <div className="flex flex-col items-end flex-shrink-0">
              <span className="text-amber-400 text-sm font-black flex items-center gap-0.5">
                <span className="text-xs">⭐</span> {ovr100}
              </span>
              <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider mt-0.5">
                OVR {ovr100 - 12}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-1 py-3 border-t border-b border-white/5 my-2">
            {stats.map((stat, i) => (
              <div key={i} className="flex flex-col items-center justify-center">
                <span className="text-[10px] text-gray-500 font-black uppercase tracking-wider">
                  {stat.label}
                </span>
                <span className="text-base font-black text-[#7cff01] mt-0.5">
                  {stat.val}
                </span>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-start">
            <span className="text-[11px] font-black text-[#7cff01]/90 flex items-center gap-1">
              <Award size={12} className="text-[#7cff01]" />
              {bottomTag}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
